// src/hooks/useScrollReveal.js
// Lightweight, dependency-free scroll-reveal engine for the public site.
// Observes every element carrying a `.reveal` / `.reveal-left` / `.reveal-right`
// / `.reveal-zoom` / `.reveal-fade` class and adds `.in-view` once it enters
// the viewport. Purely additive — does not touch app state or data logic.
import { useEffect } from "react";

export function useScrollReveal(deps = []) {
  useEffect(() => {
    const selector = ".reveal, .reveal-left, .reveal-right, .reveal-zoom, .reveal-fade";

    if (!("IntersectionObserver" in window)) {
      document.querySelectorAll(selector).forEach((el) => el.classList.add("in-view"));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("in-view");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -40px 0px" }
    );

    const observeAll = (root = document) => {
      root.querySelectorAll(selector).forEach((el) => {
        if (!el.classList.contains("in-view")) io.observe(el);
      });
    };

    observeAll();

    // Watch for elements added later (e.g. API data arriving after mount)
    const mo = new MutationObserver((mutations) => {
      mutations.forEach((m) => {
        m.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.matches?.(selector)) io.observe(node);
          node.querySelectorAll?.(selector).forEach((el) => io.observe(el));
        });
      });
    });
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      io.disconnect();
      mo.disconnect();
    };
  }, deps);
}

export default useScrollReveal;
